import {useEffect, useMemo, useState} from 'react';
import {Modal, Table, Button} from 'react-bootstrap';
import {useOpenPOs} from '../hooks';
import {formatPurchaseCurrency} from '../utils/formatPurchaseCurrency';

const SORT_DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc'
};
const DEBUG_FILTERS = false;

const normalizeValue = value => String(value ?? '').trim().toLowerCase();
const toComparableText = value => String(value ?? '').trim();
const normalizeCompanyText = value =>
  toComparableText(value).replace(/\s+/g, '').toLowerCase();
const valuesEqual = (left, right) => {
  const leftText = toComparableText(left);
  const rightText = toComparableText(right);

  const leftNum = Number(leftText);
  const rightNum = Number(rightText);
  const bothNumeric = !Number.isNaN(leftNum) && !Number.isNaN(rightNum);

  if (bothNumeric) return leftNum === rightNum;
  return normalizeValue(leftText) === normalizeValue(rightText);
};
const companyContains = (companyValue, selectedCompany) => {
  const companyText = normalizeCompanyText(companyValue);
  const selectedText = normalizeCompanyText(selectedCompany);
  if (!selectedText) return true;
  return companyText.includes(selectedText);
};

const AMOUNT_FILTER = {
  ALL: 'all',
  HAS_AMOUNT: 'has_amount',
  NO_AMOUNT: 'no_amount',
  CUSTOM: 'custom'
};

const AMOUNT_COMPARE = {
  GTE: 'gte',
  LTE: 'lte',
  EQ: 'eq'
};

const parseAmountThresholdInput = raw => {
  const s = String(raw ?? '').replace(/[$,]/g, '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const amountMatchesCustomFilter = (orderAmount, compareOp, threshold) => {
  if (threshold == null) return true;
  if (orderAmount == null) return false;
  if (compareOp === AMOUNT_COMPARE.GTE) return orderAmount >= threshold;
  if (compareOp === AMOUNT_COMPARE.LTE) return orderAmount <= threshold;
  if (compareOp === AMOUNT_COMPARE.EQ)
    return Math.abs(orderAmount - threshold) < 0.005;
  return true;
};

const ViewOpenPOsModal = ({onClose, onSelectPO}) => {
  const {openRawPOs, isLoading, error} = useOpenPOs();
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [requestorFilter, setRequestorFilter] = useState('all');
  const [amountFilterMode, setAmountFilterMode] = useState(AMOUNT_FILTER.ALL);
  const [amountCompareOp, setAmountCompareOp] = useState(AMOUNT_COMPARE.GTE);
  const [amountThresholdInput, setAmountThresholdInput] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'PONum',
    direction: SORT_DIRECTIONS.ASC
  });

  const companyScopedPOs = useMemo(() => {
    if (companyFilter === 'all') return openRawPOs;
    return openRawPOs.filter(po =>
      companyContains(po.companyID, companyFilter)
    );
  }, [openRawPOs, companyFilter]);

  const supplierScopedPOs = useMemo(() => {
    if (supplierFilter === 'all') return companyScopedPOs;
    return companyScopedPOs.filter(po => valuesEqual(po.vendor, supplierFilter));
  }, [companyScopedPOs, supplierFilter]);

  const projectScopedPOs = useMemo(() => {
    if (projectFilter === 'all') return supplierScopedPOs;
    return supplierScopedPOs.filter(
      po =>
        valuesEqual(
          `${po.projectID || ''}||${po.projDesc || ''}`,
          projectFilter
        )
    );
  }, [supplierScopedPOs, projectFilter]);

  const finalScopedPOs = useMemo(() => {
    if (requestorFilter === 'all') return projectScopedPOs;
    return projectScopedPOs.filter(po =>
      valuesEqual(po.requestorID, requestorFilter)
    );
  }, [projectScopedPOs, requestorFilter]);

  const valueFilteredPOs = useMemo(() => {
    const customThreshold =
      amountFilterMode === AMOUNT_FILTER.CUSTOM
        ? parseAmountThresholdInput(amountThresholdInput)
        : null;

    return finalScopedPOs.filter(po => {
      if (amountFilterMode === AMOUNT_FILTER.ALL) return true;
      if (amountFilterMode === AMOUNT_FILTER.HAS_AMOUNT)
        return po.orderAmount != null;
      if (amountFilterMode === AMOUNT_FILTER.NO_AMOUNT)
        return po.orderAmount == null;
      if (amountFilterMode === AMOUNT_FILTER.CUSTOM) {
        return amountMatchesCustomFilter(
          po.orderAmount,
          amountCompareOp,
          customThreshold
        );
      }
      return true;
    });
  }, [
    finalScopedPOs,
    amountFilterMode,
    amountCompareOp,
    amountThresholdInput
  ]);

  const filterOptions = useMemo(() => {
    const getUniqueSorted = (source, key) => {
      const values = source
        .map(po => toComparableText(po[key]))
        .filter(value => value !== '');
      return [...new Set(values)].sort((a, b) => a.localeCompare(b));
    };

    const projects = supplierScopedPOs
      .filter(po => po.projectID)
      .map(po => ({
        value: `${po.projectID}||${po.projDesc || ''}`,
        label: po.projDesc ? `${po.projectID} - ${po.projDesc}` : po.projectID
      }))
      .filter(
        (project, index, arr) =>
          arr.findIndex(item => item.value === project.value) === index
      )
      .sort((a, b) => a.label.localeCompare(b.label));

    return {
      companies: getUniqueSorted(openRawPOs, 'companyID'),
      suppliers: getUniqueSorted(companyScopedPOs, 'vendor'),
      projects,
      requestors: getUniqueSorted(projectScopedPOs, 'requestorID')
    };
  }, [
    openRawPOs,
    companyScopedPOs,
    supplierScopedPOs,
    projectScopedPOs
  ]);

  useEffect(() => {
    if (
      companyFilter !== 'all' &&
      !filterOptions.companies.some(company => valuesEqual(company, companyFilter))
    ) {
      setCompanyFilter('all');
    }
  }, [filterOptions.companies, companyFilter]);

  useEffect(() => {
    if (
      supplierFilter !== 'all' &&
      !filterOptions.suppliers.some(supplier =>
        valuesEqual(supplier, supplierFilter)
      )
    ) {
      setSupplierFilter('all');
    }
  }, [filterOptions.suppliers, supplierFilter]);

  useEffect(() => {
    if (
      projectFilter !== 'all' &&
      !filterOptions.projects.some(project =>
        valuesEqual(project.value, projectFilter)
      )
    ) {
      setProjectFilter('all');
    }
  }, [filterOptions.projects, projectFilter]);

  useEffect(() => {
    if (
      requestorFilter !== 'all' &&
      !filterOptions.requestors.some(requestor =>
        valuesEqual(requestor, requestorFilter)
      )
    ) {
      setRequestorFilter('all');
    }
  }, [filterOptions.requestors, requestorFilter]);

  const filteredAndSortedPOs = useMemo(() => {
    const term = normalizeValue(searchTerm);

    const filtered = valueFilteredPOs.filter(po => {
      const poNumber = normalizeValue(po.PONum);
      const flexPONum = normalizeValue(po.flexPONum);
      const vendor = normalizeValue(po.vendor);
      const project = normalizeValue(po.projectID);
      const requestor = normalizeValue(po.requestorID);
      const company = normalizeValue(po.companyID);
      const projectDesc = normalizeValue(po.projDesc);

      const matchesSearch =
        term.length === 0 ||
        poNumber.includes(term) ||
        flexPONum.includes(term) ||
        vendor.includes(term) ||
        project.includes(term) ||
        requestor.includes(term) ||
        company.includes(term) ||
        projectDesc.includes(term);
      return matchesSearch;
    });

    return filtered.sort((a, b) => {
      const left = a[sortConfig.key];
      const right = b[sortConfig.key];

      if (left == null && right == null) return 0;
      if (left == null)
        return sortConfig.direction === SORT_DIRECTIONS.ASC ? 1 : -1;
      if (right == null)
        return sortConfig.direction === SORT_DIRECTIONS.ASC ? -1 : 1;

      const leftNum = Number(left);
      const rightNum = Number(right);
      const bothNumeric = !Number.isNaN(leftNum) && !Number.isNaN(rightNum);
      const compareResult = bothNumeric
        ? leftNum - rightNum
        : normalizeValue(left).localeCompare(normalizeValue(right), undefined, {
            numeric: true
          });

      return sortConfig.direction === SORT_DIRECTIONS.ASC
        ? compareResult
        : -compareResult;
    });
  }, [
    valueFilteredPOs,
    searchTerm,
    sortConfig
  ]);

  useEffect(() => {
    if (!DEBUG_FILTERS) return;
    console.warn('[OpenPOFilters] state', {
      companyFilter,
      supplierFilter,
      projectFilter,
      requestorFilter,
      amountFilterMode,
      amountCompareOp,
      amountThresholdInput,
      searchTerm,
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction
    });
  }, [
    companyFilter,
    supplierFilter,
    projectFilter,
    requestorFilter,
    amountFilterMode,
    amountCompareOp,
    amountThresholdInput,
    searchTerm,
    sortConfig
  ]);

  useEffect(() => {
    if (!DEBUG_FILTERS) return;
    console.warn('[OpenPOFilters] optionCounts', {
      companies: filterOptions.companies.length,
      suppliers: filterOptions.suppliers.length,
      projects: filterOptions.projects.length,
      requestors: filterOptions.requestors.length
    });
  }, [filterOptions]);

  useEffect(() => {
    if (!DEBUG_FILTERS) return;
    const sampleCompanies = filteredAndSortedPOs
      .slice(0, 10)
      .map(po => toComparableText(po.companyID));
    console.warn('[OpenPOFilters] resultSet', {
      totalOpenPOs: openRawPOs.length,
      companyScoped: companyScopedPOs.length,
      supplierScoped: supplierScopedPOs.length,
      projectScoped: projectScopedPOs.length,
      finalScoped: finalScopedPOs.length,
      valueFiltered: valueFilteredPOs.length,
      renderedRows: filteredAndSortedPOs.length,
      sampleCompanies
    });
  }, [
    openRawPOs,
    companyScopedPOs,
    supplierScopedPOs,
    projectScopedPOs,
    finalScopedPOs,
    valueFilteredPOs,
    filteredAndSortedPOs
  ]);

  const renderedCompanyIDs = useMemo(() => {
    return [
      ...new Set(
        filteredAndSortedPOs.map(po => toComparableText(po.companyID)).filter(Boolean)
      )
    ];
  }, [filteredAndSortedPOs]);

  const requestSort = key => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return {
          key,
          direction:
            prev.direction === SORT_DIRECTIONS.ASC
              ? SORT_DIRECTIONS.DESC
              : SORT_DIRECTIONS.ASC
        };
      }

      return {key, direction: SORT_DIRECTIONS.ASC};
    });
  };

  const sortMarker = key => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === SORT_DIRECTIONS.ASC ? ' ▲' : ' ▼';
  };

  return (
    <>
      <div className="custom-backdrop-overlay"></div>
      <Modal show onHide={onClose} size="lg" className="view-open-pos-modal">
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title className="modal-title-custom">
            View Open Purchase Orders
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="filter-panel">
            <div className="row">
              <div className="col-md-2">
                <label htmlFor="po-search-input" className="filter-label">
                  Search
                </label>
                <input
                  id="po-search-input"
                  className="form-control"
                  placeholder="PO #, Flex PO #, Supplier, Project, Requestor"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label htmlFor="po-company-filter" className="filter-label">
                  Company
                </label>
                <select
                  id="po-company-filter"
                  className="form-control"
                  value={companyFilter}
                  onChange={e => setCompanyFilter(e.target.value)}>
                  <option value="all">All</option>
                  {filterOptions.companies.map(company => (
                    <option key={company} value={company}>
                      {company}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label htmlFor="po-supplier-filter" className="filter-label">
                  Supplier
                </label>
                <select
                  id="po-supplier-filter"
                  className="form-control"
                  value={supplierFilter}
                  onChange={e => setSupplierFilter(e.target.value)}>
                  <option value="all">All</option>
                  {filterOptions.suppliers.map(supplier => (
                    <option key={supplier} value={supplier}>
                      {supplier}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label htmlFor="po-project-filter" className="filter-label">
                  Project
                </label>
                <select
                  id="po-project-filter"
                  className="form-control"
                  value={projectFilter}
                  onChange={e => setProjectFilter(e.target.value)}>
                  <option value="all">All</option>
                  {filterOptions.projects.map(project => (
                    <option key={project.value} value={project.value}>
                      {project.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label htmlFor="po-requestor-filter" className="filter-label">
                  Requestor
                </label>
                <select
                  id="po-requestor-filter"
                  className="form-control"
                  value={requestorFilter}
                  onChange={e => setRequestorFilter(e.target.value)}>
                  <option value="all">All</option>
                  {filterOptions.requestors.map(requestor => (
                    <option key={requestor} value={requestor}>
                      {requestor}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label htmlFor="po-amount-filter" className="filter-label">
                  Value
                </label>
                <select
                  id="po-amount-filter"
                  className="form-control"
                  value={amountFilterMode}
                  onChange={e => setAmountFilterMode(e.target.value)}>
                  <option value={AMOUNT_FILTER.ALL}>All</option>
                  <option value={AMOUNT_FILTER.HAS_AMOUNT}>Has an amount</option>
                  <option value={AMOUNT_FILTER.NO_AMOUNT}>No amount</option>
                  <option value={AMOUNT_FILTER.CUSTOM}>Custom…</option>
                </select>
              </div>
            </div>
            {amountFilterMode === AMOUNT_FILTER.CUSTOM && (
              <div className="row mt-2 align-items-end">
                <div className="col-md-4">
                  <label htmlFor="po-amount-compare" className="filter-label">
                    Compare
                  </label>
                  <select
                    id="po-amount-compare"
                    className="form-control"
                    value={amountCompareOp}
                    onChange={e => setAmountCompareOp(e.target.value)}>
                    <option value={AMOUNT_COMPARE.GTE}>At least (≥)</option>
                    <option value={AMOUNT_COMPARE.LTE}>At most (≤)</option>
                    <option value={AMOUNT_COMPARE.EQ}>Equal to (=)</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label
                    htmlFor="po-amount-threshold"
                    className="filter-label">
                    Amount
                  </label>
                  <input
                    id="po-amount-threshold"
                    type="text"
                    inputMode="decimal"
                    className="form-control"
                    placeholder="e.g. 1000 — leave blank to show all"
                    value={amountThresholdInput}
                    onChange={e => setAmountThresholdInput(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {isLoading ? (
            <p className="text-center">Loading open purchase orders...</p>
          ) : error ? (
            <p className="text-danger text-center">Error: {error}</p>
          ) : filteredAndSortedPOs.length === 0 ? (
            <div className="empty-state">No purchase orders found.</div>
          ) : (
            <div className="table-container">
              <Table bordered hover responsive className="po-table">
                <thead>
                  <tr>
                    <th onClick={() => requestSort('PONum')}>
                      PO Number{sortMarker('PONum')}
                    </th>
                    <th onClick={() => requestSort('flexPONum')}>
                      Flex PO Number{sortMarker('flexPONum')}
                    </th>
                    <th onClick={() => requestSort('vendor')}>
                      Supplier Name{sortMarker('vendor')}
                    </th>
                    <th onClick={() => requestSort('companyID')}>
                      Company{sortMarker('companyID')}
                    </th>
                    <th onClick={() => requestSort('projectID')}>
                      Project{sortMarker('projectID')}
                    </th>
                    <th onClick={() => requestSort('requestorID')}>
                      Requestor{sortMarker('requestorID')}
                    </th>
                    <th onClick={() => requestSort('orderAmount')}>
                      Value{sortMarker('orderAmount')}
                    </th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedPOs.map((po, index) => (
                    <tr
                      key={`${toComparableText(po.companyID)}-${toComparableText(po.PONum)}-${toComparableText(po.projectID)}-${toComparableText(po.requestorID)}-${index}`}>
                      <td>{po.PONum}</td>
                      <td>{po.flexPONum || ''}</td>
                      <td>{po.vendor || 'N/A'}</td>
                      <td>{po.companyID}</td>
                      <td>
                        {po.projectID}
                        {po.projDesc ? ` - ${po.projDesc}` : ''}
                      </td>
                      <td>{po.requestorID || 'N/A'}</td>
                      <td>
                        {formatPurchaseCurrency(
                          po.orderAmount,
                          po.currencyCode
                        )}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() =>
                            onSelectPO({
                              companyID: po.companyID,
                              PONum: po.PONum,
                              orderAmount: po.orderAmount ?? null,
                              currencyCode: po.currencyCode ?? null
                            })
                          }>
                          Select PO
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <span className="results-count">
            Showing {filteredAndSortedPOs.length} of {openRawPOs.length} open
            POs
          </span>
          <Button className="custom-close-button" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
        {DEBUG_FILTERS && (
          <div className="debug-panel">
            <strong>DEBUG</strong> | company: {companyFilter} | supplier:{' '}
            {supplierFilter} | project: {projectFilter} | requestor:{' '}
            {requestorFilter} | value: {amountFilterMode}
            {amountFilterMode === AMOUNT_FILTER.CUSTOM
              ? ` (${amountCompareOp} ${amountThresholdInput || '—'})`
              : ''}{' '}
            | rows:{' '}
            {filteredAndSortedPOs.length} | rendered companies:{' '}
            {renderedCompanyIDs.join(', ') || 'none'}
          </div>
        )}

        <style>{`
          .custom-backdrop-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background-color: rgba(0, 0, 0, 0.3);
            z-index: 1040;
          }

          .view-open-pos-modal {
            z-index: 1050;
            position: relative;
            color: #4a4a4a;
            max-width: 90vw;
            top: 4vh;
          }

          .view-open-pos-modal .modal-content {
            border: none;
            border-radius: 8px;
            background-color: #ffffff;
            box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.15);
          }

          .modal-header-custom {
            background-color: #f8f9fa;
            padding: 16px 24px;
            border-bottom: 1px solid #dee2e6;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
          }

          .modal-title-custom {
            font-size: 1.5rem;
            font-weight: 600;
            color: #343a40;
          }

          .filter-panel {
            background: #f7f9fc;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 14px;
          }

          .filter-label {
            display: block;
            font-size: 12px;
            color: #506079;
            margin-bottom: 4px;
            min-height: 18px;
          }

          .table-container {
            max-height: 58vh;
            overflow-y: auto;
            border: 1px solid #ebedf2;
            border-radius: 8px;
          }

          .empty-state {
            background: #f7f9fc;
            border: 1px solid #ebedf2;
            border-radius: 8px;
            padding: 24px;
            text-align: center;
            color: #5f6d7e;
            font-weight: 500;
          }

          .po-table {
            margin-bottom: 0;
          }

          .po-table thead th {
            position: sticky;
            top: 0;
            background-color: #eef2f7;
            cursor: pointer;
            z-index: 2;
            white-space: nowrap;
          }

          .results-count {
            color: #5f6d7e;
            font-size: 13px;
          }

          .custom-close-button {
            background-color: #3264af;
            color: white;
            border: none;
            border-radius: 8px;
            min-width: 100px;
          }

          .custom-close-button:hover {
            background-color: #0a3b84 !important;
            color: white !important;
          }

          .debug-panel {
            border-top: 1px solid #ebedf2;
            background: #fff8e1;
            color: #5d4037;
            font-size: 12px;
            padding: 8px 12px;
          }
        `}</style>
      </Modal>
    </>
  );
};

export default ViewOpenPOsModal;
